import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  Match,
  MatchWithGames,
  MatchConfig,
  Game,
  RoundScore,
  ParticipantTotal,
  TrumpSuit,
} from '@/types';
import { MATCH_FORMAT_CONFIG, checkMatchWinner } from '@/types';
import { matchRepo } from '@/db/repositories/matchRepo';
import { gameRepo } from '@/db/repositories/gameRepo';
import { roundRepo } from '@/db/repositories/roundRepo';
import {
  calculateTotals,
  checkWinner,
  validateRoundScores,
  applyInsideOutsideRule,
} from '@/lib/scoring';

interface MatchState {
  activeMatch: MatchWithGames | null;
  isLoading: boolean;
  error: string | null;
}

interface MatchActions {
  loadActiveMatch: (matchId: string) => Promise<void>;
  createMatch: (config: MatchConfig) => Promise<Match | null>;
  startNextGame: () => Promise<Game | null>;
  addRound: (
    scores: RoundScore[],
    trumpSuit: TrumpSuit,
    trumpCallerId: string
  ) => Promise<boolean>;
  deleteLastRound: () => Promise<boolean>;
  completeCurrentGame: (winnerId: string) => Promise<boolean>;
  abandonMatch: () => Promise<boolean>;
  clearActiveMatch: () => void;
  clearError: () => void;
  refreshMatch: () => Promise<void>;
}

type MatchStore = MatchState & MatchActions;

export const useMatchStore = create<MatchStore>()(
  persist(
    immer((set, get) => ({
      // State
      activeMatch: null,
      isLoading: false,
      error: null,

      // Actions
      loadActiveMatch: async (matchId: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const matchWithGames = await matchRepo.getWithGames(matchId);
          set((state) => {
            state.activeMatch = matchWithGames;
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to load match';
            state.isLoading = false;
          });
        }
      },

      createMatch: async (config: MatchConfig) => {
        set((state) => {
          state.error = null;
        });

        try {
          // Create the match
          const match = await matchRepo.create({
            format: config.format,
            mode: config.mode,
            targetScore: config.targetScore,
            participants: config.participants,
          });

          // Create the first game
          const game = await gameRepo.createForMatch(match);

          // Build the match with games object
          const matchWithGames: MatchWithGames = {
            ...match,
            games: [game],
            gameWins: Object.fromEntries(
              config.participants.map((p) => [p.id, 0])
            ),
            gamesNeededToWin: MATCH_FORMAT_CONFIG[config.format].gamesToWin,
            currentGame: {
              ...game,
              rounds: [],
              totals: config.participants.map((p) => ({
                participantId: p.id,
                participantName: p.name,
                regularPoints: 0,
                declarationPoints: 0,
                totalPoints: 0,
              })),
            },
          };

          set((state) => {
            state.activeMatch = matchWithGames;
          });

          return match;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to create match';
          });
          return null;
        }
      },

      startNextGame: async () => {
        const { activeMatch } = get();
        if (!activeMatch || activeMatch.status === 'completed') {
          set((state) => {
            state.error = 'Cannot start new game - match is not active';
          });
          return null;
        }

        // Check if there's already an active game
        if (activeMatch.currentGame?.status === 'active') {
          set((state) => {
            state.error = 'There is already an active game';
          });
          return null;
        }

        try {
          const game = await gameRepo.createForMatch(activeMatch);

          // Reload the match to get updated state
          const updatedMatch = await matchRepo.getWithGames(activeMatch.id);

          set((state) => {
            state.activeMatch = updatedMatch;
          });

          return game;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to start new game';
          });
          return null;
        }
      },

      addRound: async (
        scores: RoundScore[],
        trumpSuit: TrumpSuit,
        trumpCallerId: string
      ) => {
        const { activeMatch } = get();
        if (!activeMatch?.currentGame) {
          set((state) => {
            state.error = 'No active game';
          });
          return false;
        }

        const currentGame = activeMatch.currentGame;

        // Apply inside/outside rule
        const { scores: finalScores } = applyInsideOutsideRule(scores, trumpCallerId, currentGame.mode);

        // Validate scores
        const validation = validateRoundScores(currentGame.mode, finalScores);
        if (!validation.valid) {
          set((state) => {
            state.error = validation.errors.join('. ');
          });
          return false;
        }

        try {
          const round = await roundRepo.create(
            currentGame.id,
            finalScores,
            trumpSuit,
            trumpCallerId
          );

          // Update local state
          set((state) => {
            if (state.activeMatch?.currentGame) {
              state.activeMatch.currentGame.rounds.push(round);
              state.activeMatch.currentGame.totals = calculateTotals(
                state.activeMatch.currentGame.participants,
                state.activeMatch.currentGame.rounds
              ) as ParticipantTotal[];
            }
          });

          // Check for game winner
          const updatedState = get();
          if (updatedState.activeMatch?.currentGame) {
            const winnerId = checkWinner(
              updatedState.activeMatch.currentGame.totals,
              updatedState.activeMatch.currentGame.targetScore
            );
            if (winnerId) {
              await get().completeCurrentGame(winnerId);
            }
          }

          return true;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to add round';
          });
          return false;
        }
      },

      deleteLastRound: async () => {
        const { activeMatch } = get();
        if (
          !activeMatch?.currentGame ||
          activeMatch.currentGame.rounds.length === 0
        ) {
          return false;
        }

        const lastRound =
          activeMatch.currentGame.rounds[
            activeMatch.currentGame.rounds.length - 1
          ];

        try {
          await roundRepo.delete(lastRound.id);

          set((state) => {
            if (state.activeMatch?.currentGame) {
              state.activeMatch.currentGame.rounds =
                state.activeMatch.currentGame.rounds.filter(
                  (r) => r.id !== lastRound.id
                );
              state.activeMatch.currentGame.totals = calculateTotals(
                state.activeMatch.currentGame.participants,
                state.activeMatch.currentGame.rounds
              ) as ParticipantTotal[];
            }
          });

          return true;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to delete round';
          });
          return false;
        }
      },

      completeCurrentGame: async (winnerId: string) => {
        const { activeMatch } = get();
        if (!activeMatch?.currentGame) {
          return false;
        }

        try {
          // Complete the game in DB
          await gameRepo.complete(activeMatch.currentGame.id, winnerId);

          // Reload match to get updated state
          const updatedMatch = await matchRepo.getWithGames(activeMatch.id);
          if (!updatedMatch) {
            return false;
          }

          // Check if match is complete
          const matchWinnerId = checkMatchWinner(
            updatedMatch.gameWins,
            updatedMatch.gamesNeededToWin
          );

          if (matchWinnerId) {
            // Complete the match
            await matchRepo.complete(activeMatch.id, matchWinnerId);
            const completedMatch = await matchRepo.getWithGames(activeMatch.id);
            set((state) => {
              state.activeMatch = completedMatch;
            });
          } else {
            set((state) => {
              state.activeMatch = updatedMatch;
            });
          }

          return true;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to complete game';
          });
          return false;
        }
      },

      abandonMatch: async () => {
        const { activeMatch } = get();
        if (!activeMatch) {
          return false;
        }

        try {
          await matchRepo.delete(activeMatch.id);
          set((state) => {
            state.activeMatch = null;
          });
          return true;
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to abandon match';
          });
          return false;
        }
      },

      clearActiveMatch: () => {
        set((state) => {
          state.activeMatch = null;
        });
      },

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      refreshMatch: async () => {
        const { activeMatch } = get();
        if (!activeMatch) {
          return;
        }

        try {
          const updatedMatch = await matchRepo.getWithGames(activeMatch.id);
          set((state) => {
            state.activeMatch = updatedMatch;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : 'Failed to refresh match';
          });
        }
      },
    })),
    {
      name: 'belot-active-match',
      partialize: (state) => ({
        activeMatch: state.activeMatch,
      }),
    }
  )
);
